import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { resetPassword } from "@/lib/api";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !token) return;
    setBusy(true);
    try {
      const res = await resetPassword({ token, new_password: password });
      toast.success(res.message);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — request a new link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 pt-24 pb-16" data-testid="reset-password-page">
      <Reveal className="w-full max-w-md">
        <div className="bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8 text-center">
          <h1 className="font-display font-bold text-3xl text-teal">A Fresh Password</h1>
          <div className="mt-4 h-px w-16 bg-gold mx-auto" />
          {done ? (
            <div data-testid="reset-success">
              <p className="mt-6 text-sm text-teal/70 leading-relaxed">Your password is updated. Your stamps are right where you left them.</p>
              <Link to="/loyalty" data-testid="reset-signin-link"
                className="mt-6 inline-block rounded-full bg-teal text-cream px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-teal-deep transition-colors">
                Sign In
              </Link>
            </div>
          ) : !token ? (
            <p className="mt-6 text-sm text-teal/70 leading-relaxed" data-testid="reset-invalid">This link is missing its key — please request a fresh one from the sign-in page.</p>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="mt-6 flex flex-col gap-4 text-left">
              <label className="text-xs uppercase tracking-[0.25em] text-teal/60" htmlFor="new-password">New password</label>
              <div className="relative">
                <input id="new-password" required type={showPw ? "text" : "password"} minLength={6} placeholder="6+ characters" data-testid="reset-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 pr-8 text-teal placeholder:text-teal/40 transition-colors" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="reset-toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-teal/40 hover:text-gold transition-colors">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button type="submit" disabled={busy} data-testid="reset-submit"
                className="mt-4 w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? "Updating..." : <>Set New Password <KeyRound size={15} /></>}
              </button>
            </form>
          )}
        </div>
      </Reveal>
    </div>
  );
};

export default ResetPassword;
